/**
 * Post-authentication navigation helper.
 *
 * Fetches the current user's workspace from the backend and updates
 * the login store. Returns the workspace slug to navigate to, or
 * '/' if the user needs onboarding.
 *
 * Shared between CallbackPage (post-sign-in resolver) and AuthGatewayPage.
 */

import { isAxiosError } from 'axios'
import { takeOAuthReturn } from './oauthReturn'
import postGetCurrentUser from '@/lib/api/features/authentication/api/postGetCurrentUser'
import getWorkspaces from '@/lib/api/features/authentication/api/getWorkspaces'
import { acceptInvite } from '@/lib/api/features/workspace-invites/api/acceptInvite'
import useLoginStore from '@/store/LoginStore'
import { setUserContext } from '@/lib/monitoring/sentry'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'

export interface PostAuthResult {
  navigateTo: string
}

/**
 * Fetch current user from backend, populate login store, and return
 * the route to navigate to.
 *
 * @throws Error on non-404 API failures (caller should handle)
 */
export async function resolvePostAuth(): Promise<PostAuthResult> {
  const pendingInviteToken = sessionStorage.getItem('pendingInviteToken')
    || localStorage.getItem('pendingInviteToken')

  try {
    const resp = await postGetCurrentUser()
    const user = resp.data

    // Update the login store
    const store = useLoginStore.getState()
    store.setUsername(user.username)
    store.setUserId(user.userId)
    store.setWorkspaceId(user.workspaceId)
    store.setWorkspaceSlug(user.workspaceSlug)
    store.setWorkspaceKey(user.workspaceKey)
    store.setWorkspaceSec(user.workspaceSec)
    store.setWorkspaceType(user.workspaceType)
    store.setUserRole(user.role ?? 'member')

    // Set user context in Sentry
    setUserContext({
      id: user.userId,
      email: user.username,
      workspaceSlug: user.workspaceSlug,
    })

    logger.logEvent(LogCategory.AUTH_CALLBACK, 'post_auth.success', {
      userId: user.userId,
      workspaceSlug: user.workspaceSlug,
    })

    // Existing user with pending invite → accept it inline
    if (pendingInviteToken) {
      logger.logEvent(LogCategory.AUTH_CALLBACK, 'post_auth.accepting_invite')
      try {
        const inviteResult = await acceptInvite(pendingInviteToken)
        sessionStorage.removeItem('pendingInviteToken')
        localStorage.removeItem('pendingInviteToken')
        logger.logEvent(LogCategory.AUTH_CALLBACK, 'post_auth.invite_accepted', {
          workspaceSlug: inviteResult.workspaceSlug,
        })

        // Fetch updated workspace list so the store has the newly joined workspace
        try {
          const workspacesResp = await getWorkspaces()
          store.setWorkspaces(workspacesResp.data)

          // Switch store to the invited workspace so AppLayout doesn't redirect away
          const targetWs = workspacesResp.data.find(
            (w) => w.workspaceSlug === inviteResult.workspaceSlug,
          )
          if (targetWs) {
            store.switchWorkspace(targetWs)
          }
        } catch {
          // If workspace fetch fails, still navigate — AppLayout will retry on mount
        }

        return { navigateTo: `/${inviteResult.workspaceSlug}` }
      } catch (inviteErr) {
        // Invite failed (expired, wrong email, etc.) — clear token and continue normally
        logger.logEvent(LogCategory.AUTH_CALLBACK, 'post_auth.invite_accept_failed', {
          error: inviteErr instanceof Error ? inviteErr.message : 'unknown',
        })
        sessionStorage.removeItem('pendingInviteToken')
        localStorage.removeItem('pendingInviteToken')
      }
    }

    // An MCP client connection was waiting on this sign-in: finish it first.
    const oauthReturn = takeOAuthReturn()
    if (oauthReturn) return { navigateTo: oauthReturn }

    // Fetch the user's workspace list
    let workspaces: Awaited<ReturnType<typeof getWorkspaces>>['data'] = []
    try {
      const workspacesResp = await getWorkspaces()
      workspaces = workspacesResp.data
      store.setWorkspaces(workspaces)
    } catch (wsErr) {
      // If fetch fails, navigate to default workspace
      logger.logEvent(LogCategory.AUTH_CALLBACK, 'post_auth.workspaces_fetch_failed')
      return { navigateTo: `/${user.workspaceSlug}` }
    }

    // Multiple workspaces → show workspace selection page
    if (workspaces.length > 1) {
      return { navigateTo: '/workspaces' }
    }

    // Single workspace → go directly
    return { navigateTo: `/${user.workspaceSlug}` }
  } catch (err) {
    // User not found → needs onboarding
    if (isAxiosError(err) && err.response?.status === 404) {
      logger.logEvent(LogCategory.AUTH_CALLBACK, 'post_auth.needs_onboarding')
      return { navigateTo: '/onboarding' }
    }

    // Re-throw other errors for the caller to handle
    throw err
  }
}
